import { useEffect, useState } from 'react';
import {
  BrowserRouter, Routes, Route, Navigate, useLocation,
} from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import { storage } from '../api/client';
import Splash from '../components/Splash';
import AppShell from '../components/AppShell';

import LandingPage from '../pages/LandingPage';
import PrivacyPage from '../pages/legal/PrivacyPage';
import DeleteAccountPage from '../pages/legal/DeleteAccountPage';

import OnboardingPage from '../pages/auth/OnboardingPage';
import RoleSelectPage from '../pages/auth/RoleSelectPage';
import LoginPage from '../pages/auth/LoginPage';
import OtpPage from '../pages/auth/OtpPage';
import ProfileSetupPage from '../pages/auth/ProfileSetupPage';
import PartnerRegisterPage from '../pages/auth/PartnerRegisterPage';

import HomePage from '../pages/user/HomePage';
import ExplorePage from '../pages/user/ExplorePage';
import SearchPage from '../pages/user/SearchPage';
import CategoryDetailPage from '../pages/user/CategoryDetailPage';
import PartnerDetailPage from '../pages/user/PartnerDetailPage';
import ServiceDetailPage from '../pages/user/ServiceDetailPage';
import ProductDetailPage from '../pages/user/ProductDetailPage';
import BookServicePage from '../pages/user/BookServicePage';
import CartPage from '../pages/user/CartPage';
import CheckoutPage from '../pages/user/CheckoutPage';
import ActivityPage from '../pages/user/ActivityPage';
import OrderDetailPage from '../pages/user/OrderDetailPage';
import BookingDetailPage from '../pages/user/BookingDetailPage';
import AddressesPage from '../pages/user/AddressesPage';
import AddAddressPage from '../pages/user/AddAddressPage';
import ProfilePage from '../pages/user/ProfilePage';
import EditProfilePage from '../pages/user/EditProfilePage';
import WorkProfilePage from '../pages/user/WorkProfilePage';
import JobSeekersPage from '../pages/user/JobSeekersPage';
import SeekerDetailPage from '../pages/user/SeekerDetailPage';
import AppearancePage from '../pages/user/AppearancePage';
import ListingsPage from '../pages/user/ListingsPage';
import WishesPage from '../pages/user/WishesPage';
import SupportPage from '../pages/user/SupportPage';
import TicketChatPage from '../pages/user/TicketChatPage';
import ListingDetailPage from '../pages/user/ListingDetailPage';
import PostListingPage from '../pages/user/PostListingPage';
import NotificationsPage from '../pages/user/NotificationsPage';
import WriteReviewPage from '../pages/user/WriteReviewPage';
import MyReviewsPage from '../pages/user/MyReviewsPage';
import JobsPage from '../pages/user/JobsPage';
import JobDetailPage from '../pages/user/JobDetailPage';
import MyApplicationsPage from '../pages/user/MyApplicationsPage';
import SavedJobsPage from '../pages/user/SavedJobsPage';
import ShowroomTourPage from '../pages/user/ShowroomTourPage';
import HotelsPage from '../pages/user/HotelsPage';
import HotelDetailPage from '../pages/user/HotelDetailPage';
import MyStaysPage from '../pages/user/MyStaysPage';
import MyHotelPage from '../pages/partner/MyHotelPage';
import BikeServicePage from '../pages/user/BikeServicePage';
import MyBikesPage from '../pages/user/MyBikesPage';
import BookBikeServicePage from '../pages/user/BookBikeServicePage';
import ServiceJobPage from '../pages/user/ServiceJobPage';
import ServiceHistoryPage from '../pages/user/ServiceHistoryPage';
import GarageJobsPage from '../pages/partner/GarageJobsPage';
import MasterMenuPage from '../pages/partner/MasterMenuPage';
import MyCategoriesPage from '../pages/partner/MyCategoriesPage';
import BankDetailsPage from '../pages/partner/BankDetailsPage';
import ShowroomTourEditPage from '../pages/partner/ShowroomTourPage';
import VehiclesPage from '../pages/user/VehiclesPage';
import VehicleDetailPage from '../pages/user/VehicleDetailPage';
import PostVehiclePage from '../pages/user/PostVehiclePage';
import MyVehiclesPage from '../pages/user/MyVehiclesPage';
import RideBookingPage from '../pages/user/RideBookingPage';
import RideTrackingPage from '../pages/user/RideTrackingPage';
import DesirePage from '../pages/user/DesirePage';
import SellOnKaamwalaPage from '../pages/user/SellOnKaamwalaPage';
import LocationPage from '../pages/user/LocationPage';

import PartnerDashboardPage from '../pages/partner/PartnerDashboardPage';
import PartnerOrdersPage from '../pages/partner/PartnerOrdersPage';
import PartnerBookingsPage from '../pages/partner/PartnerBookingsPage';
import PartnerCataloguePage from '../pages/partner/PartnerCataloguePage';
import AddCatalogueItemPage from '../pages/partner/AddCatalogueItemPage';
import PartnerJobsPage from '../pages/partner/PartnerJobsPage';
import PostJobPage from '../pages/partner/PostJobPage';
import PartnerProfilePage from '../pages/partner/PartnerProfilePage';
import DriverRidesPage from '../pages/partner/DriverRidesPage';

const ONBOARDED_KEY = 'kw_onboarded';

/** Every navigation should start at the top, as a page load would. */
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0 }); }, [pathname]);
  return null;
};

/**
 * Gate for the signed-in customer app. A partner without a business is sent to
 * finish registration first, exactly as the mobile app's RootNavigator does.
 */
const UserRoute = ({ children }) => {
  const { user, isPartner, hasBusiness, mode } = useAuth();
  const { pathname } = useLocation();

  if (!user) return <Navigate to="/get-started" replace state={{ from: pathname }} />;
  // Only redirect someone who is actually on the partner side. Gating on the
  // role alone trapped every account that had ever opened partner signup.
  if (isPartner && !hasBusiness && mode === 'partner') {
    return <Navigate to="/partner/register" replace />;
  }
  return <AppShell side="user">{children}</AppShell>;
};

/** Gate for the partner dashboard. */
const PartnerRoute = ({ children }) => {
  const { user, isPartner, hasBusiness } = useAuth();
  const { pathname } = useLocation();

  if (!user) return <Navigate to="/login?role=partner" replace state={{ from: pathname }} />;
  if (!isPartner) return <Navigate to="/sell" replace />;
  if (!hasBusiness) return <Navigate to="/partner/register" replace />;
  return <AppShell side="partner">{children}</AppShell>;
};

/** Signed-in people never need the login pages again. */
const GuestRoute = ({ children }) => {
  const { user, isPartner, hasBusiness, mode } = useAuth();
  if (user) {
    if (isPartner && !hasBusiness && mode === 'partner') {
      return <Navigate to="/partner/register" replace />;
    }
    return <Navigate to={isPartner && mode === 'partner' ? '/partner' : '/home'} replace />;
  }
  return children;
};

/**
 * Role select, unless this browser has never seen onboarding.
 *
 * The flag is read here rather than in AppRouter's body so it is re-evaluated on
 * every render - reading it once left /get-started bouncing back to onboarding
 * after the slides had already been finished.
 */
const StartRoute = () => (
  storage.get(ONBOARDED_KEY) === '1'
    ? <RoleSelectPage />
    : <Navigate to="/onboarding" replace />
);

/** Anyone signed in reaches this; a partner without a business must finish it. */
const AuthedRoute = ({ children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/get-started" replace />;
  return children;
};

const AppRouter = () => {
  const { booting } = useAuth();
  const [showSplash, setShowSplash] = useState(true);
  const [leaving, setLeaving] = useState(false);

  // The splash stays up until the session is restored, with a short minimum so
  // the brand animation is never a flash on a fast connection.
  useEffect(() => {
    if (booting) return undefined;
    const t = setTimeout(() => setLeaving(true), 700);
    const t2 = setTimeout(() => setShowSplash(false), 1040);
    return () => { clearTimeout(t); clearTimeout(t2); };
  }, [booting]);

  if (showSplash) return <Splash leaving={leaving} />;

  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        {/* Public */}
        <Route path="/" element={<LandingPage />} />
        {/*
          Open to anyone, signed in or not, app installed or not: Google Play
          requires both of these to be reachable straight from a browser.
        */}
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/deleteaccount" element={<DeleteAccountPage />} />
        <Route path="/delete-account" element={<Navigate to="/deleteaccount" replace />} />
        <Route path="/get-started" element={<GuestRoute><StartRoute /></GuestRoute>} />
        <Route path="/onboarding" element={<GuestRoute><OnboardingPage /></GuestRoute>} />
        <Route path="/login" element={<GuestRoute><LoginPage /></GuestRoute>} />
        <Route path="/verify" element={<GuestRoute><OtpPage /></GuestRoute>} />
        <Route path="/profile-setup" element={<ProfileSetupPage />} />

        {/* Partner onboarding sits outside the shell - there is no business yet. */}
        <Route
          path="/partner/register"
          element={<AuthedRoute><PartnerRegisterPage /></AuthedRoute>}
        />

        {/* Customer */}
        <Route path="/home" element={<UserRoute><HomePage /></UserRoute>} />
        <Route path="/services" element={<UserRoute><ExplorePage /></UserRoute>} />
        <Route path="/search" element={<UserRoute><SearchPage /></UserRoute>} />
        <Route path="/category/:id" element={<UserRoute><CategoryDetailPage /></UserRoute>} />
        <Route path="/business/:id" element={<UserRoute><PartnerDetailPage /></UserRoute>} />
        <Route path="/service/:id" element={<UserRoute><ServiceDetailPage /></UserRoute>} />
        <Route path="/product/:id" element={<UserRoute><ProductDetailPage /></UserRoute>} />
        <Route path="/book/:serviceId" element={<UserRoute><BookServicePage /></UserRoute>} />
        <Route path="/cart" element={<UserRoute><CartPage /></UserRoute>} />
        <Route path="/checkout/:partnerId" element={<UserRoute><CheckoutPage /></UserRoute>} />
        <Route path="/activity" element={<UserRoute><ActivityPage /></UserRoute>} />
        <Route path="/orders/:id" element={<UserRoute><OrderDetailPage /></UserRoute>} />
        <Route path="/bookings/:id" element={<UserRoute><BookingDetailPage /></UserRoute>} />
        <Route path="/addresses" element={<UserRoute><AddressesPage /></UserRoute>} />
        <Route path="/addresses/new" element={<UserRoute><AddAddressPage /></UserRoute>} />
        <Route path="/addresses/:id/edit" element={<UserRoute><AddAddressPage /></UserRoute>} />
        <Route path="/account" element={<UserRoute><ProfilePage /></UserRoute>} />
        <Route path="/profile/edit" element={<UserRoute><EditProfilePage /></UserRoute>} />
        <Route path="/work-profile" element={<UserRoute><WorkProfilePage /></UserRoute>} />
        <Route path="/people" element={<UserRoute><JobSeekersPage /></UserRoute>} />
        <Route path="/people/:id" element={<UserRoute><SeekerDetailPage /></UserRoute>} />
        <Route path="/profile/appearance" element={<UserRoute><AppearancePage /></UserRoute>} />
        <Route path="/special-days" element={<WishesPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/support/:id" element={<TicketChatPage />} />
        <Route path="/property" element={<ListingsPage kind="property" />} />
        <Route path="/property/post" element={<UserRoute><PostListingPage kind="property" /></UserRoute>} />
        <Route path="/animals/post" element={<UserRoute><PostListingPage kind="animal" /></UserRoute>} />
        <Route path="/property/:id" element={<ListingDetailPage kind="property" />} />
        <Route path="/animals" element={<ListingsPage kind="animal" />} />
        <Route path="/animals/:id" element={<ListingDetailPage kind="animal" />} />
        {/* Ordinary second-hand things, sold by people rather than shops. */}
        <Route path="/buy-sell" element={<ListingsPage kind="item" />} />
        <Route path="/buy-sell/post" element={<UserRoute><PostListingPage kind="item" /></UserRoute>} />
        <Route path="/buy-sell/:id" element={<ListingDetailPage kind="item" />} />
        <Route path="/notifications" element={<AuthedRoute><AppShell><NotificationsPage /></AppShell></AuthedRoute>} />
        <Route path="/review" element={<UserRoute><WriteReviewPage /></UserRoute>} />
        <Route path="/my-reviews" element={<UserRoute><MyReviewsPage /></UserRoute>} />
        <Route path="/jobs" element={<UserRoute><JobsPage /></UserRoute>} />
        <Route path="/jobs/:id" element={<UserRoute><JobDetailPage /></UserRoute>} />
        <Route path="/my-applications" element={<UserRoute><MyApplicationsPage /></UserRoute>} />
        <Route path="/saved-jobs" element={<UserRoute><SavedJobsPage /></UserRoute>} />
        <Route path="/business/:id/tour" element={<ShowroomTourPage />} />
        <Route path="/hotels" element={<HotelsPage />} />
        <Route path="/hotels/:id" element={<HotelDetailPage />} />
        <Route path="/my-stays" element={<UserRoute><MyStaysPage /></UserRoute>} />
        <Route path="/partner/hotel" element={<PartnerRoute><MyHotelPage /></PartnerRoute>} />
        <Route path="/bike-service" element={<BikeServicePage />} />
        <Route path="/bike-service/book" element={<UserRoute><BookBikeServicePage /></UserRoute>} />
        <Route path="/my-vehicles-service" element={<UserRoute><MyBikesPage /></UserRoute>} />
        <Route path="/my-vehicles-service/:id/history" element={<UserRoute><ServiceHistoryPage /></UserRoute>} />
        <Route path="/service-jobs/:id" element={<UserRoute><ServiceJobPage /></UserRoute>} />
        <Route path="/partner/service-jobs" element={<PartnerRoute><GarageJobsPage /></PartnerRoute>} />
        <Route path="/partner/master-menu" element={<PartnerRoute><MasterMenuPage /></PartnerRoute>} />
        <Route path="/partner/categories" element={<PartnerRoute><MyCategoriesPage /></PartnerRoute>} />
        {/* Everyone who earns through KAAMWALA needs somewhere to be paid. */}
        <Route path="/partner/bank" element={<PartnerRoute><BankDetailsPage /></PartnerRoute>} />
        <Route path="/partner/showroom-tour" element={<PartnerRoute><ShowroomTourEditPage /></PartnerRoute>} />
        <Route path="/vehicles" element={<UserRoute><VehiclesPage /></UserRoute>} />
        <Route path="/vehicles/post" element={<UserRoute><PostVehiclePage /></UserRoute>} />
        <Route path="/vehicles/:id" element={<UserRoute><VehicleDetailPage /></UserRoute>} />
        <Route path="/my-vehicles" element={<UserRoute><MyVehiclesPage /></UserRoute>} />
        <Route path="/ride" element={<UserRoute><RideBookingPage /></UserRoute>} />
        <Route path="/ride/:id" element={<UserRoute><RideTrackingPage /></UserRoute>} />
        <Route path="/desire" element={<UserRoute><DesirePage /></UserRoute>} />
        <Route path="/sell" element={<UserRoute><SellOnKaamwalaPage /></UserRoute>} />
        <Route path="/location" element={<UserRoute><LocationPage /></UserRoute>} />

        {/* Partner */}
        <Route path="/partner" element={<PartnerRoute><PartnerDashboardPage /></PartnerRoute>} />
        <Route path="/partner/orders" element={<PartnerRoute><PartnerOrdersPage /></PartnerRoute>} />
        <Route path="/partner/bookings" element={<PartnerRoute><PartnerBookingsPage /></PartnerRoute>} />
        <Route path="/partner/catalogue" element={<PartnerRoute><PartnerCataloguePage /></PartnerRoute>} />
        <Route path="/partner/catalogue/new" element={<PartnerRoute><AddCatalogueItemPage /></PartnerRoute>} />
        <Route path="/partner/rides" element={<PartnerRoute><DriverRidesPage /></PartnerRoute>} />
        <Route path="/partner/jobs" element={<PartnerRoute><PartnerJobsPage /></PartnerRoute>} />
        <Route path="/partner/jobs/new" element={<PartnerRoute><PostJobPage /></PartnerRoute>} />
        <Route path="/partner/business" element={<PartnerRoute><PartnerProfilePage /></PartnerRoute>} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
};

export default AppRouter;
